import { useState, useMemo, useCallback } from 'react';
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
} from '@mui/x-data-grid';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { useUsers } from '../hooks/useUsers';
import { useToggleUserStatus, useDeleteUser } from '../hooks/useUserMutations';
import { UserStatusChip } from './UserStatusChip';
import { UserRoleChip } from './UserRoleChip';
import { useTableSearchParams } from '@/hooks/useTableSearchParams';
import type { UserListItem, UserQueryParams } from '../types/user.types';
import type { UserRole } from '@/types/auth';

interface UserListProps {
  filters: UserQueryParams;
  onEdit: (user: UserListItem) => void;
  onResetPassword: (user: UserListItem) => void;
  canManageUsers: boolean;
}

export function UserList({ filters, onEdit, onResetPassword, canManageUsers }: UserListProps) {
  const { page, pageSize, setPage, setPageSize } = useTableSearchParams();
  const paginationModel = useMemo(() => ({ page, pageSize }), [page, pageSize]);
  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      if (model.page !== page) setPage(model.page);
      if (model.pageSize !== pageSize) setPageSize(model.pageSize);
    },
    [page, pageSize, setPage, setPageSize]
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<UserListItem | null>(null);

  const queryParams: UserQueryParams = {
    ...filters,
    page: paginationModel.page + 1, // API is 1-indexed
    pageSize: paginationModel.pageSize,
  };

  const { data, isLoading, isError } = useUsers(queryParams);
  const toggleStatus = useToggleUserStatus();
  const deleteUser = useDeleteUser();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: UserListItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleEdit = () => {
    if (selectedUser) {
      onEdit(selectedUser);
    }
    handleMenuClose();
  };

  const handleResetPassword = () => {
    if (selectedUser) {
      onResetPassword(selectedUser);
    }
    handleMenuClose();
  };

  const handleToggleStatus = () => {
    if (selectedUser) {
      const newStatus = selectedUser.status === 'active' ? 'suspended' : 'active';
      toggleStatus.mutate({ id: selectedUser.id, status: newStatus });
    }
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmUser) {
      deleteUser.mutate(deleteConfirmUser.id);
      setDeleteConfirmUser(null);
    }
  };

  const columns: GridColDef<UserListItem>[] = useMemo(
    () => [
      {
        field: 'fullName',
        headerName: 'Name',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'roles',
        headerName: 'Roles',
        width: 220,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', height: '100%' }}>
            {(params.value as string[] ?? [params.row.role]).map((r: string) => (
              <UserRoleChip key={r} role={r as UserRole} />
            ))}
          </Box>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        renderCell: (params) => <UserStatusChip status={params.value} />,
      },
      {
        field: 'lastLoginAt',
        headerName: 'Last Login',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          const date = params.value ? new Date(params.value) : null;
          return date ? date.toLocaleDateString() : 'Never';
        },
      },
      ...(canManageUsers ? [{
        field: 'actions',
        headerName: 'Actions',
        width: 50,
        sortable: false,
        filterable: false,
        renderCell: (params: { row: UserListItem }) => (
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, params.row)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ),
      } as GridColDef<UserListItem>] : []),
    ],
    [canManageUsers]
  );

  if (isError) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">Failed to load users</Typography>
      </Box>
    );
  }

  return (
    <>
      <DataGrid
        rows={data?.items ?? []}
        columns={columns}
        loading={isLoading}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        rowCount={data?.totalCount ?? 0}
        pageSizeOptions={[20, 50, 100]}
        sx={{ height: 600 }}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {canManageUsers && (
          <MenuItem onClick={handleEdit}>Edit User</MenuItem>
        )}
        <MenuItem onClick={handleToggleStatus}>
          {selectedUser?.status === 'active' ? 'Suspend' : 'Activate'}
        </MenuItem>
        <MenuItem onClick={handleResetPassword}>Reset Password</MenuItem>
        {canManageUsers && (
          <MenuItem
            onClick={() => {
              setDeleteConfirmUser(selectedUser);
              handleMenuClose();
            }}
            sx={{ color: 'error.main' }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={!!deleteConfirmUser}
        onClose={() => setDeleteConfirmUser(null)}
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirmUser?.fullName ?? deleteConfirmUser?.email}</strong>?
            This action can be reversed by an Admin or User Manager.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmUser(null)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
